import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function usePackkySetting() {
  const { api, AdminId } = useGlobalContext();

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getAdminId = () => {
    if (AdminId) return String(AdminId);

    try {
      const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
      return (
        userData?.adminMeta?.adminId ||
        userData?.adminId ||
        userData?.userid ||
        ""
      );
    } catch {
      return "";
    }
  };

  // --------- 1) FETCH OFFERS ----------
  const fetchOffersApi = async (setOffers, setLoading, silent = false) => {
    if (!silent) setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getAllDriverOffers", {
        userid: "",
      });

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.response)
          ? res.data.response
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];

        setOffers(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch offers");
      }

      setOffers([]);
      return [];
    } catch (error) {
      console.error("fetchOffersApi error:", error);
      toast.error("Error fetching offers");
      setOffers([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 2) CREATE OFFER ----------
  const createOfferApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/insertDriverOffers", body);

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Offer created");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to create offer");
      }

      return false;
    } catch (error) {
      console.error("createOfferApi error:", error);
      toast.error("Error creating offer");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 3) DELETE OFFER ----------
  const deleteOfferApi = async (offerId, setLoading, onSuccess) => {
    if (!offerId) {
      toast.error("Invalid offer ID");
      return false;
    }

    setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/deleteDriverOffers", {
        offerid: String(offerId),
      });

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Offer deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to delete offer");
      }

      return false;
    } catch (error) {
      console.error("deleteOfferApi error:", error);
      toast.error("Error deleting offer");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 4) ADD INCENTIVE ----------
  const addIncentiveApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/Driver/v1.0/insertDriverOffersIncentive",
        body
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Incentive added");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add incentive");
      }

      return false;
    } catch (error) {
      console.error("addIncentiveApi error:", error);
      toast.error("Error adding incentive");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 5) FETCH VEHICLES ----------
  const fetchVehicleDetailsApi = async (
    setVehicles,
    setLoading,
    silent = false
  ) => {
    if (!silent) setLoading(true);

    try {
      const payload = {
        userId: String(getAdminId()),
      };

      const res = await api.post(
        "/api/ClientAdmin/v1.0/getAllVehicleDetails",
        payload
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setVehicles(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch vehicles");
      }

      setVehicles([]);
      return [];
    } catch (error) {
      console.error("fetchVehicleDetailsApi error:", error);
      toast.error("Error fetching vehicles");
      setVehicles([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 6) FETCH DISTANCE FARES ----------
  const fetchDistanceFaresApi = async (
    setFares,
    setLoading,
    silent = false
  ) => {
    if (!silent) setLoading(true);

    try {
      const payload = {
        userId: String(getAdminId()),
      };

      const res = await api.post(
        "/api/ClientAdmin/v1.0/getAllDistanceFares",
        payload
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setFares(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch distance fares"
        );
      }

      setFares([]);
      return [];
    } catch (error) {
      console.error("fetchDistanceFaresApi error:", error);
      toast.error("Error fetching distance fares");
      setFares([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 7) CREATE VEHICLE ----------
  const createVehicleApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v2.0/insertVehicleDetails",
        body,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Vehicle added");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add vehicle");
      }

      return false;
    } catch (error) {
      console.error("createVehicleApi error:", error);
      toast.error("Error adding vehicle");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 8) DELETE VEHICLE ----------
  const deleteVehicleApi = async (vehicleId, setLoading, onSuccess) => {
    if (!vehicleId) {
      toast.error("Invalid vehicle ID");
      return false;
    }

    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v2.0/deleteVehicleDetails",
        {
          vehicleId: String(vehicleId),
        },
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Vehicle deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to delete vehicle");
      }

      return false;
    } catch (error) {
      console.error("deleteVehicleApi error:", error);
      toast.error("Error deleting vehicle");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 9) INSERT / UPDATE DISTANCE FARE ----------
  const saveDistanceFareApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v1.0/insertDistanceFares",
        body,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Distance fare saved");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to save fare");
      }

      return false;
    } catch (error) {
      console.error("saveDistanceFareApi error:", error);
      toast.error("Error saving fare");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 10) DELETE DISTANCE FARE ----------
  const deleteDistanceFareApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/ClientAdmin/v1.0/deleteDistanceFares",
        body,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Distance fare deleted"
        );

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to delete distance fare"
        );
      }

      return false;
    } catch (error) {
      console.error("deleteDistanceFareApi error:", error);
      toast.error("Error deleting distance fare");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 11) FETCH PERFORMANCE POINTS BONUS ----------
  const fetchPerformancePointsBonusApi = async (
    setBonusList,
    setLoading,
    silent = false
  ) => {
    if (!silent) setLoading(true);

    try {
      const payload = {
        userId: String(getAdminId()),
      };

      const res = await api.post(
        "/api/Admin/v1.0/getdriverPerformmancePointsBonus",
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.response)
          ? res.data.response
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];

        setBonusList(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch performance bonus"
        );
      }

      setBonusList([]);
      return [];
    } catch (error) {
      console.error("fetchPerformancePointsBonusApi error:", error);
      toast.error("Error fetching performance bonus");
      setBonusList([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 12) INSERT PERFORMANCE POINTS BONUS ----------
  const insertPerformancePointsBonusApi = async (
    body,
    setLoading,
    onSuccess
  ) => {
    setLoading(true);

    try {
      const payload = {
        adminId: String(body?.adminId || getAdminId()),
        fromPoints: String(body?.fromPoints || ""),
        toPoints: String(body?.toPoints || ""),
        bonus: String(body?.bonus || ""),
        status: String(body?.status || "Y"),
      };

      const res = await api.post(
        "/api/Admin/v1.0/insertPerformmancePointsBonus",
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Bonus inserted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to insert performance bonus"
        );
      }

      return false;
    } catch (error) {
      console.error("insertPerformancePointsBonusApi error:", error);
      toast.error("Error inserting performance bonus");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 13) UPDATE PERFORMANCE POINTS BONUS STATUS ----------
  const updatePerformancePointsBonusApi = async (
    body,
    setLoading,
    onSuccess
  ) => {
    setLoading(true);

    try {
      const payload = {
        adminId: String(body?.adminId || getAdminId()),
        bonusId: String(body?.bonusId || ""),
        status: String(body?.status || "N"),
      };

      const res = await api.post(
        "/api/Admin/v1.0/updatePerformmancePointsBonus",
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Bonus updated");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to update performance bonus"
        );
      }

      return false;
    } catch (error) {
      console.error("updatePerformancePointsBonusApi error:", error);
      toast.error("Error updating performance bonus");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchOffersApi,
    createOfferApi,
    deleteOfferApi,
    addIncentiveApi,

    // Vehicle related
    fetchVehicleDetailsApi,
    fetchDistanceFaresApi,
    createVehicleApi,
    deleteVehicleApi,
    saveDistanceFareApi,
    deleteDistanceFareApi,
    fetchPerformancePointsBonusApi,
    insertPerformancePointsBonusApi,
    updatePerformancePointsBonusApi,
  };
}